import {ValueObject} from '@s-ui/domain'

import {ListBreadcrumbSeoValueObject} from '../../seo/ValueObjects/ListBreadcrumbSeoValueObject.js'
import {CanonicalValueObject} from './CanonicalValueObject.js'
import {MetaDataSearchValueObject} from './MetadataSearchValueObject.js'
import {RequestSearchValueObject} from './RequestSearchValueObject.js'

export class DataSearchValueObject extends ValueObject {
  #breadcrumbs
  #canonical
  #listingSectionId
  #metaData
  #search

  constructor({breadcrumbs, canonical, listingSectionId, metaData, search}) {
    super()
    this.#breadcrumbs = breadcrumbs
    this.#canonical = canonical
    this.#listingSectionId = listingSectionId
    this.#metaData = metaData
    this.#search = search
  }

  static create({breadcrumbs, canonical, listingSectionId, metaData, search}) {
    return new DataSearchValueObject({
      breadcrumbs: ListBreadcrumbSeoValueObject.create(breadcrumbs),
      canonical,
      listingSectionId,
      metaData: MetaDataSearchValueObject.create(metaData),
      search: RequestSearchValueObject.create(search)
    })
  }

  static createFromApi({config, data, url = ''}) {
    const {LISTING_SECTION_IDS} = config.get('COMMON')

    // Necessary until backend standardizes the listingSectionId for renting vehicles in stock page
    const listingSectionId =
      data.listingSectionId === LISTING_SECTION_IDS.RENTING_VEHICLES || url.includes('/renting/concesionario/')
        ? LISTING_SECTION_IDS.RENTING_VEHICLES
        : data.listingSectionId || LISTING_SECTION_IDS.DEFAULT_LISTING_SECTION_ID

    const breadcrumbs =
      listingSectionId === LISTING_SECTION_IDS.RENTING_VEHICLES
        ? ListBreadcrumbSeoValueObject.createFromRentingAdsList({config, fields: data.breadcrumbs || [], url})
        : ListBreadcrumbSeoValueObject.createFromAdsList({config, fields: data.breadcrumbs || []})

    const metaData = MetaDataSearchValueObject.createFromPrimitive({metaData: data.metaData || {}})

    const search = RequestSearchValueObject.createFromApi({
      config,
      listingSectionId,
      search: data.search,
      url
    })

    const canonical = CanonicalValueObject.createFromApi({
      config,
      canonical: data.canonical,
      url
    })

    return new DataSearchValueObject({
      breadcrumbs,
      canonical: canonical.value,
      listingSectionId,
      metaData,
      search
    })
  }

  get breadcrumbs() {
    return this.#breadcrumbs.value
  }

  get canonical() {
    return this.#canonical
  }

  get listingSectionId() {
    return this.#listingSectionId
  }

  get metaData() {
    return this.#metaData.value
  }

  get search() {
    return this.#search.value
  }

  get searchForFilters() {
    return this.#search.searchForFilters
  }

  get value() {
    return {
      breadcrumbs: this.#breadcrumbs.value,
      canonical: this.#canonical,
      listingSectionId: this.#listingSectionId,
      metaData: this.#metaData.value,
      search: this.#search.value
    }
  }

  getCanonicalPathname() {
    const canonicalUrl = new URL(this.#canonical)
    return canonicalUrl?.pathname
  }

  toFiltersSearch() {
    return {
      breadcrumbs: this.#breadcrumbs.value,
      canonical: this.#canonical,
      listingSectionId: this.#listingSectionId,
      metaData: this.#metaData.value,
      search: this.#search.searchForFilters
    }
  }

  toJSON() {
    return {
      breadcrumbs: this.#breadcrumbs.toJSON(),
      canonical: this.#canonical,
      listingSectionId: this.#listingSectionId,
      metaData: this.#metaData.toJSON(),
      search: this.#search.toJSON()
    }
  }
}
